import React from 'react';

function LoadingSpinner({ 
  size = 'medium', 
  fullPage = false, 
  text = 'Loading...', 
  variant = 'primary',
  className = '' 
}) {
  const sizeClasses = {
    small: 'h-4 w-4 border-2',
    medium: 'h-8 w-8 border-3',
    large: 'h-12 w-12 border-4',
    xlarge: 'h-16 w-16 border-4'
  };

  const variantClasses = {
    primary: 'border-silver-mist border-t-fresh-leaf',
    white: 'border-steel/30 border-t-crystal-white',
    secondary: 'border-silver-mist border-t-woodland-moss',
    success: 'border-silver-mist border-t-forest-fern',
    danger: 'border-silver-mist border-t-error'
  };
  
  const spinnerClass = `animate-spin ${sizeClasses[size]} ${variantClasses[variant]} ${className}`;
  
  if (fullPage) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-crystal-white bg-opacity-90 z-50">
        <div className={`animate-spin h-16 w-16 border-4 ${variantClasses[variant]}`}></div>
        <p className="mt-6 text-steel text-lg font-medium">{text}</p>
      </div>
    );
  }
  
  return (
    <div className="flex items-center justify-center">
      <div className={spinnerClass}></div>
    </div>
  );
}

// Loading button component
export function LoadingButton({ 
  children, 
  isLoading, 
  loadingText = 'Loading...', 
  variant = 'primary',
  size = 'medium',
  disabled,
  className = '',
  ...props 
}) {
  const baseClasses = 'inline-flex items-center justify-center font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed border';
  
  const sizeClasses = {
    small: 'px-3 py-1.5 text-sm',
    medium: 'px-4 py-2 text-sm',
    large: 'px-6 py-3 text-base'
  };

  const variantClasses = {
    primary: 'text-crystal-white bg-fresh-leaf hover:bg-forest-fern focus:ring-fresh-leaf/25 border-fresh-leaf',
    secondary: 'text-forest-fern bg-crystal-white hover:bg-silver-mist focus:ring-fresh-leaf/25 border-woodland-moss',
    danger: 'text-crystal-white bg-error hover:bg-red-700 focus:ring-error/25 border-error',
    success: 'text-crystal-white bg-forest-fern hover:bg-woodland-moss focus:ring-forest-fern/25 border-forest-fern',
    outline: 'text-graphite bg-crystal-white border-steel hover:bg-silver-mist focus:ring-fresh-leaf/25'
  };

  const finalClassName = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${className}`;

  return (
    <button 
      className={finalClassName}
      disabled={isLoading || disabled}
      {...props}
    >
      {isLoading && (
        <LoadingSpinner 
          size="small" 
          variant={variant === 'primary' || variant === 'danger' || variant === 'success' ? 'white' : 'primary'}
          className="mr-2" 
        />
      )}
      {isLoading ? loadingText : children}
    </button>
  );
}

// Skeleton loading component
export function SkeletonLoader({ className = '', lines = 1, height = 'h-4' }) {
  return (
    <div className={`animate-pulse ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <div 
          key={index} 
          className={`bg-silver-mist ${height} ${index > 0 ? 'mt-2' : ''}`}
        />
      ))}
    </div>
  );
}

export default LoadingSpinner;
